export const PDF_MAX_BYTES = 50 * 1024 * 1024;
export const PDF_MAX_PAGES = 200;

function abortIfNeeded(signal) {
  if (signal?.aborted) throw new DOMException('Conversion canceled.', 'AbortError');
}

export function validatePdfBytes(bytes) {
  if (!(bytes instanceof Uint8Array) || bytes.byteLength === 0) return 'Choose a PDF file first.';
  if (bytes.byteLength > PDF_MAX_BYTES) return 'The PDF is larger than 50 MiB.';
  const signature = new TextDecoder('latin1').decode(bytes.subarray(0, 5));
  return signature === '%PDF-' ? '' : 'The selected file is not a valid PDF.';
}

export function parsePageRange(value, pageCount) {
  const count = Number(pageCount);
  if (!Number.isInteger(count) || count < 1 || count > PDF_MAX_PAGES) {
    throw new Error(`This PDF has an unsupported page count. The limit is ${PDF_MAX_PAGES} pages.`);
  }
  const source = String(value ?? '').trim();
  if (!source) return Array.from({ length: count }, (_, index) => index + 1);
  const pages = new Set();
  for (const rawPart of source.split(',')) {
    const part = rawPart.trim();
    if (!part) throw new Error('Page ranges cannot contain an empty item.');
    const range = part.match(/^(\d+)(?:\s*-\s*(\d+))?$/);
    if (!range) throw new Error(`Invalid page range item: ${part}`);
    const start = Number(range[1]);
    const end = range[2] ? Number(range[2]) : start;
    if (!Number.isInteger(start) || !Number.isInteger(end) || start < 1 || end < start || end > count) {
      throw new Error(`Page range ${part} is outside 1-${count}.`);
    }
    for (let page = start; page <= end; page += 1) pages.add(page);
  }
  return [...pages].sort((left, right) => left - right);
}

function listPages(pages) {
  const values = [...new Set(pages)].sort((left, right) => left - right);
  if (values.length <= 8) return values.join(', ');
  return `${values.slice(0, 7).join(', ')} and ${values.length - 7} more`;
}

function stripHeadingMarkers(markdown) {
  return String(markdown || '').replace(/^(#{1,6})\s+(.+)$/gm, '$2');
}

export function buildPdfResult(processed, classification, selectedPages, options = {}) {
  const ocrPages = new Set((classification?.pagesNeedingOcr || []).map((page) => Number(page) + 1));
  const selectedOcrPages = selectedPages.filter((page) => ocrPages.has(page));
  let markdown = String(processed?.markdown || '');
  if (options.headingMode === 'plain') markdown = stripHeadingMarkers(markdown);
  const warnings = [];
  if (selectedOcrPages.length) {
    warnings.push(`Pages ${listPages(selectedOcrPages)} contain scanned or image-based content. OCR is not performed, so text from those pages may be missing.`);
  }
  if (classification?.pdfType === 'Mixed' && selectedOcrPages.length) {
    warnings.push('This PDF is mixed: text extraction is available for selected text pages, while selected image pages remain unprocessed.');
  } else if (classification?.pdfType === 'Scanned' && selectedOcrPages.length) {
    warnings.push('This PDF is scanned. The result is not a complete text conversion because OCR is not included.');
  } else if (classification?.pdfType === 'ImageBased' && selectedOcrPages.length) {
    warnings.push('This PDF contains image-based pages. The result is not a complete text conversion because OCR is not included.');
  }
  const tablePages = processed?.layout?.pagesWithTables || [];
  if (tablePages.length) warnings.push(`Tables were detected on page${tablePages.length === 1 ? '' : 's'} ${listPages(tablePages)}; review their Markdown structure.`);
  const columnPages = processed?.layout?.pagesWithColumns || [];
  if (columnPages.length) warnings.push(`Multiple columns were detected on page${columnPages.length === 1 ? '' : 's'} ${listPages(columnPages)}; reading order may need review.`);
  if (processed?.hasEncodingIssues) warnings.push('The PDF reports text encoding issues; review special characters in the result.');
  if (!markdown.trim() && !selectedOcrPages.length) warnings.push('No extractable text was returned for the selected pages.');
  return { markdown, warnings, assets: [] };
}

export function validatePdfOptions(options = {}) {
  const headingMode = String(options.headingMode || 'preserve');
  if (!['preserve', 'plain'].includes(headingMode)) return 'Choose a supported heading mode.';
  if (options.includePageBreaks !== undefined && typeof options.includePageBreaks !== 'boolean') return 'The page separator option is invalid.';
  return '';
}

export function abortPdfIfNeeded(signal) {
  abortIfNeeded(signal);
}
